/**
 * Split phone number into country code and local number.
 * Mirrors libs/nexi_core/utils/guard_phone_number.py logic.
 * @param {string} phoneNumber - e.g. "+85297500300", "85297500300", "(852)97500300"
 * @param {string} defaultDialingCode - default "852"
 * @returns {{ countryCode: string, phoneNumber: string }}
 */
export function splitPhoneNumber(phoneNumber, defaultDialingCode = "852") {
  const dialingCodeLengthMap = {
    "852": 8,
    "86": 11,
    "65": 8,
    "1": 10,
  };

  if (!phoneNumber) {
    return { countryCode: `+${defaultDialingCode}`, phoneNumber: "" };
  }

  let normalized = phoneNumber.replace(/\s/g, "").replace(/[()]/g, "").trim();

  const parenMatch = normalized.match(/^\(\+?([0-9]+)\)(.+)$/);
  if (parenMatch) {
    return {
      countryCode: `+${parenMatch[1]}`,
      phoneNumber: parenMatch[2].trim(),
    };
  }

  if (normalized.startsWith("+")) {
    for (const [code, length] of Object.entries(dialingCodeLengthMap)) {
      const fullCode = `+${code}`;
      if (normalized.startsWith(fullCode) && normalized.length === length + fullCode.length) {
        return {
          countryCode: fullCode,
          phoneNumber: normalized.substring(fullCode.length).trim(),
        };
      }
    }
  }

  if (normalized.includes("-")) {
    const [codePart, numPart] = normalized.split("-", 2);
    const code = codePart.replace("+", "");
    return {
      countryCode: `+${code}`,
      phoneNumber: numPart.trim(),
    };
  }

  for (const [code, length] of Object.entries(dialingCodeLengthMap)) {
    if (normalized.startsWith(code) && normalized.length === length + code.length) {
      return {
        countryCode: `+${code}`,
        phoneNumber: normalized.substring(code.length).trim(),
      };
    }
  }

  const expectedLen = dialingCodeLengthMap[defaultDialingCode];
  if (normalized.length === expectedLen) {
    return {
      countryCode: `+${defaultDialingCode}`,
      phoneNumber: normalized,
    };
  }

  const codesByLength = Object.keys(dialingCodeLengthMap).sort((a, b) => b.length - a.length);
  for (const code of codesByLength) {
    const fullCode = normalized.startsWith("+") ? `+${code}` : code;
    if (normalized.startsWith(fullCode)) {
      return {
        countryCode: `+${code}`,
        phoneNumber: normalized.substring(fullCode.length).replace(/\D/g, ""),
      };
    }
  }

  return { countryCode: `+${defaultDialingCode}`, phoneNumber: normalized.replace(/\D/g, "") };
}
